// Documentation :https://reactrouter.com/en/v6.3.0/getting-started/installation

import React from 'react';
import {
  ACCESS_DENIED_PAGE_ROUTE,
  CREATE_CASE_PAGE_ROUTE,
  LIST_CASE_PAGE_ROUTE,
  CASE_DETAILS_PAGE_ROUTE,
  HOME_ROUTE,
  ADMIN_PAGE_ROUTE,
  ADMIN_CHILD_ROUTES,
  ANONYMOUS_CREATE_CASE_PAGE_ROUTE,
} from 'src/constants/UrlConstants';

import CaseList from 'src/pages/CaseList';
import CreateCase from 'src/pages/CreateCase';
import NotAuthorized from 'src/pages/NotAuthorized';
import CaseDetails from 'src/pages/CaseDetails';
import ProtectedRoute from './helpers/ProtectedRoute';
import { AuthenticatedRoute } from './helpers/AuthenticatedRoute';
import { SustainabilityCreateCaseForm } from 'src/pages/ExternalLandingPage/programs/sustainability';
import { CSAMLandingPage } from 'src/pages/ExternalLandingPage/programs/csam';
import { PaymentServicesComplaintPage } from 'src/pages/ExternalLandingPage/programs/paymentServicesComplaint';
import { RouteObject } from 'react-router';
import {
  PAGE_NAME,
  PAGE_SCOPE,
  UI_CLIENTS,
} from 'src/constants/GlobalConstants';
import { PageProvider } from 'src/state/providers/PageProvider';
import { Admin } from 'src/pages/Admin';
import { AddUser } from 'src/self-service/pages/AddUser';
import { ListUser } from 'src/self-service/pages/ListUser';
import { DSAPOCLandingPage } from 'src/pages/ExternalLandingPage/programs/dsaPointOfContact';
import { AnonymousCreateCasePage } from 'src/pages/CreateCase/AnonymousCreateCasePage';

/**
 * Defines the base route of the CPR Application
 */
export const baseAppRoutes: RouteObject[] = [
  {
    path: HOME_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.LIST_CASE}
        >
          <CaseList />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: LIST_CASE_PAGE_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.LIST_CASE}
        >
          <CaseList />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: CREATE_CASE_PAGE_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CREATE_CASE}
        >
          <CreateCase />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  { path: ACCESS_DENIED_PAGE_ROUTE, element: <NotAuthorized /> },
  {
    path: CASE_DETAILS_PAGE_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CASE_SUMMARY}
        >
          <CaseDetails />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: ADMIN_PAGE_ROUTE,
    element: (
      <ProtectedRoute allowedClients={[UI_CLIENTS.VICI]}>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.ADMIN}
        >
          <Admin />
        </PageProvider>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <AddUser />,
      },
      {
        path: ADMIN_CHILD_ROUTES.LIST_PAGE.relativeUrl,
        element: <ListUser />,
      },
    ],
  },
];

export const sustainabilityAppRoutes: RouteObject[] = [
  {
    path: HOME_ROUTE,
    element: (
      <PageProvider
        pageScope={PAGE_SCOPE.PUBLIC}
        pageName={PAGE_NAME.ANONYMOUS_CREATE_CASE}
      >
        <SustainabilityCreateCaseForm />
      </PageProvider>
    ),
  },
  {
    path: ANONYMOUS_CREATE_CASE_PAGE_ROUTE,
    element: (
      <PageProvider
        pageScope={PAGE_SCOPE.PUBLIC}
        pageName={PAGE_NAME.ANONYMOUS_CREATE_CASE}
      >
        <AnonymousCreateCasePage />
      </PageProvider>
    ),
  },
  {
    path: LIST_CASE_PAGE_ROUTE,
    element: (
      <AuthenticatedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.LIST_CASE}
        >
          <CaseList />
        </PageProvider>
      </AuthenticatedRoute>
    ),
  },
  {
    path: CREATE_CASE_PAGE_ROUTE,
    element: (
      <AuthenticatedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CREATE_CASE}
        >
          <CreateCase />
        </PageProvider>
      </AuthenticatedRoute>
    ),
  },
  { path: ACCESS_DENIED_PAGE_ROUTE, element: <NotAuthorized /> },
  {
    path: CASE_DETAILS_PAGE_ROUTE,
    element: (
      <AuthenticatedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CASE_SUMMARY}
        >
          <CaseDetails />
        </PageProvider>
      </AuthenticatedRoute>
    ),
  },
];

export const reportCsamAppRoutes: RouteObject[] = [
  {
    path: HOME_ROUTE,
    element: (
      <PageProvider
        pageScope={PAGE_SCOPE.PUBLIC}
        pageName={PAGE_NAME.ANONYMOUS_CREATE_CASE}
      >
        <CSAMLandingPage />
      </PageProvider>
    ),
  },
  { path: ACCESS_DENIED_PAGE_ROUTE, element: <NotAuthorized /> },
];

export const article11DsaAppRoutes: RouteObject[] = [
  {
    path: HOME_ROUTE,
    element: (
      <PageProvider
        pageScope={PAGE_SCOPE.PUBLIC}
        pageName={PAGE_NAME.ANONYMOUS_CREATE_CASE}
      >
        <DSAPOCLandingPage />
      </PageProvider>
    ),
  },
  { path: ACCESS_DENIED_PAGE_ROUTE, element: <NotAuthorized /> },
];

export const SPIMAppRoutes: RouteObject[] = [
  {
    path: HOME_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.LIST_CASE}
        >
          <CaseList />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: LIST_CASE_PAGE_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.LIST_CASE}
        >
          <CaseList />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  {
    path: CREATE_CASE_PAGE_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CREATE_CASE}
        >
          <CreateCase />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
  { path: ACCESS_DENIED_PAGE_ROUTE, element: <NotAuthorized /> },
  {
    path: CASE_DETAILS_PAGE_ROUTE,
    element: (
      <ProtectedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CASE_SUMMARY}
        >
          <CaseDetails />
        </PageProvider>
      </ProtectedRoute>
    ),
  },
];

export const paymentServicesComplaintAppRoutes: RouteObject[] = [
  {
    path: HOME_ROUTE,
    element: (
      <AuthenticatedRoute>
        <PageProvider
          pageScope={PAGE_SCOPE.AUTHENTICATED}
          pageName={PAGE_NAME.CREATE_CASE}
        >
          <PaymentServicesComplaintPage />
        </PageProvider>
      </AuthenticatedRoute>
    ),
  },
  { path: ACCESS_DENIED_PAGE_ROUTE, element: <NotAuthorized /> },
];
